import {
    Autocomplete,
    Box,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import reDiagnosis, { IReDiagData } from "dto/reDiagnosis.dto";
import reDiagnosisService from "services/api/reDiagnosis.service";
import useApiRequest, { isLoading } from "hooks/useApiRequest";
import useQueryParams from "hooks/useQueryParams";
import { useEncounter } from "pages/encounter/encounter.provider";
import CheckIcon from "@mui/icons-material/Check";
import theme from "theme";
import { clinicDTO } from "dto/his/clinicDTO";
import { get, orderBy, uniq } from "lodash";

type diagType =
    | "principal"
    | "comorbidity"
    | "complication"
    | "other"
    | "externalCause";

const ReDiagnosis = () => {
    const [hospitalNumber] = useQueryParams("hn");
    const {
        insertOrReplaceDiagnosis,
        removeDiagnosis,
        clinicData,
        allDiagnosis,
        service
    } = useEncounter();

    const [getReDiagAll, reDiagData, fetchStatus] = useApiRequest(
        reDiagnosisService.getAll
    );

    const [tempData, setTempData] = useState<string[]>([]);
    const [, setCurrentIndex] = useState<number | null>(null);

    const [ward, setWard] = useState<clinicDTO | null>(null);

    useEffect(() => {
        const allTerm = uniq(allDiagnosis.map((i) => i?.term ?? ""));
        setTempData(allTerm);
    }, [allDiagnosis]);

    const fetchData = useMemo(() => {
        return getReDiagAll;
    }, [getReDiagAll]);

    const redxList = useMemo(() => {
        const result = reDiagData?.data ?? [];

        return orderBy(result, "registerDate", "desc");
    }, [reDiagData?.data]);

    const [selectedData, setSelectedData] = useState<reDiagnosis[]>([]);

    useEffect(() => {
        fetchData(hospitalNumber, {});
    }, [fetchData, hospitalNumber]);

    const filteredClinic = useMemo(() => {
        const visitedClinic = uniq(
            (reDiagData?.data ?? []).map((i) => i.visit?.service)
        );

        const _visitedClinic = uniq([...visitedClinic]);

        return (Array.isArray(clinicData) ? clinicData : [])
            .map((i) => {
                if (_visitedClinic.includes(i.clinicId)) {
                    return {
                        ...i,
                        clinicName: `${i.clinicName} (Visited)`
                    };
                } else {
                    return { ...i };
                }
            })
            .filter((j) => _visitedClinic.includes(j.clinicId));
    }, [clinicData, reDiagData?.data]);

    useEffect(() => {
        setSelectedData([]);
        const clinic = (Array.isArray(clinicData) ? clinicData : []).find(
            (j) => j.clinicId === service
        );

        if (clinic) {
            setWard(clinic);
            const latestList = get(
                redxList.filter((i) => i.visit?.service === service),
                "0"
            );
            if (latestList) {
                setSelectedData([latestList]);
            }
        }
    }, [clinicData, redxList, service]);

    const getDiagType = (type: string) => {
        let _type: diagType = "comorbidity";

        switch (type) {
            case "Principal":
                _type = "principal";
                break;
            case "Comorbidity":
                _type = "comorbidity";
                break;
            case "Complication":
                _type = "complication";
                break;
            case "Others":
                _type = "other";
                break;
            case "External Cause":
                _type = "externalCause";
                break;
            default:
                _type = "comorbidity";
                break;
        }
        return _type;
    };

    const handleSelectDetail = (item: IReDiagData) => {
        const _type = getDiagType(item.DiagType);
        if (!tempData.includes(item.ICDDesc)) {
            insertOrReplaceDiagnosis(_type, {
                term: item.ICDDesc,
                icd10: [
                    {
                        code: (item?.ICDCode ?? "").trim(),
                        term: item.ICDDesc
                    }
                ],
                version: "ICD10-WHO-2016"
            });
            setTempData([...tempData, item.ICDDesc]);
        } else {
            removeDiagnosis(_type, {
                term: item.ICDDesc,
                icd10: [
                    {
                        code: (item?.ICDCode ?? "").trim(),
                        term: item.ICDDesc
                    }
                ],
                version: "ICD10-WHO-2016",
                conceptId: "",
                stack: ""
            });

            setTempData((prevData) =>
                prevData.filter((temp) => temp !== item.ICDDesc)
            );
        }
    };

    const handleSelectAll = (itemData: IReDiagData[]) => {
        const allSelected = itemData.every((item) =>
            tempData.includes(item.ICDDesc)
        );
        if (allSelected) {
            // Deselect all
            itemData.forEach((item) => {
                const _type = getDiagType(item.DiagType);

                removeDiagnosis(_type, {
                    term: item.ICDDesc,
                    icd10: [
                        {
                            code: (item?.ICDCode ?? "").trim(),
                            term: item.ICDDesc
                        }
                    ],
                    version: "ICD10-WHO-2016",
                    conceptId: "",
                    stack: ""
                });
            });
            setTempData((prevData) =>
                prevData.filter(
                    (temp) => !itemData.some((item) => item.ICDDesc === temp)
                )
            );
        } else {
            // Select all
            itemData.forEach((item) => {
                const _type = getDiagType(item.DiagType);

                insertOrReplaceDiagnosis(_type, {
                    term: item.ICDDesc,
                    icd10: [
                        {
                            code: (item?.ICDCode ?? "").trim(),
                            term: item.ICDDesc
                        }
                    ],
                    version: "ICD10-WHO-2016"
                });
            });
            setTempData((prevData) => [
                ...new Set([
                    ...prevData,
                    ...itemData.map((item) => item.ICDDesc)
                ])
            ]);
        }
    };

    return (
        <>
            <Stack
                direction={"column"}
                gap={1}
                sx={{
                    paddingTop: "6px"
                }}
            >
                <Autocomplete
                    getOptionLabel={(option) => option.clinicName}
                    options={filteredClinic}
                    value={ward}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Clinic"
                            variant="outlined"
                        />
                    )}
                    onChange={(_e, newValue) => {
                        setWard(newValue!);
                        setSelectedData([]);
                        const latestList = get(
                            redxList.filter(
                                (i) => i.visit?.service === newValue?.clinicId
                            ),
                            "0"
                        );
                        if (latestList) {
                            setSelectedData([latestList]);
                        }
                    }}
                />

                <Autocomplete
                    options={redxList.filter((i) => {
                        if (ward) {
                            return i.visit?.service === ward.clinicId;
                        } else {
                            return true;
                        }
                    })}
                    value={selectedData}
                    multiple
                    getOptionLabel={(option) => {
                        const date = dayjs(option.registerDate).format(
                            "DD/MM/BBBB"
                        );
                        const service =
                            (Array.isArray(clinicData) ? clinicData : []).find(
                                (i) => i.clinicId === option.visit?.service
                            )?.clinicName ?? "-";
                        const label = `${date} Ward: ${service}`;
                        return label;
                    }}
                    renderOption={(props, option) => {
                        const date = dayjs(option.registerDate).format(
                            "DD/MM/BBBB"
                        );
                        const service =
                            (Array.isArray(clinicData) ? clinicData : []).find(
                                (i) => i.clinicId === option.visit?.service
                            )?.clinicName ?? "-";
                        const label = `${date} ${service}`;
                        return (
                            <li {...props} key={option.transactionNumber}>
                                <Box width="100%">
                                    <Typography>{label}</Typography>
                                    <Typography>
                                        {option.visit?.doctorName}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="History List"
                            variant="outlined"
                        />
                    )}
                    onChange={(_, newValue) => {
                        setSelectedData(newValue ?? []);
                    }}
                    // filterOptions={(options) => options}
                />

                {!isLoading(fetchStatus) ? (
                    <Stack
                        direction={"column-reverse"}
                        gap={1}
                        alignItems={"center"}
                    >
                        {selectedData.map((item, index) => {
                            const service =
                                (Array.isArray(clinicData)
                                    ? clinicData
                                    : []
                                ).find(
                                    (i) => i.clinicId === item.visit?.service
                                )?.clinicName ?? "-";
                            return (
                                <Stack
                                    key={index}
                                    width={"100%"}
                                    direction={"column"}
                                    gap={1}
                                    sx={{
                                        padding: "1.5rem 1rem",
                                        border: "1px solid #CCC",
                                        borderRadius: "12px"
                                    }}
                                >
                                    <Typography
                                        fontWeight={500}
                                        color="GrayText"
                                    >
                                        Date:{" "}
                                        {dayjs(item.registerDate).format(
                                            "DD/MM/BBBB"
                                        )}
                                    </Typography>

                                    <Stack direction={"row"}>
                                        <Typography
                                            fontSize={14}
                                            color="GrayText"
                                        >
                                            TXN: {item.transactionNumber}
                                        </Typography>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            sx={{ mx: 1 }}
                                        />
                                        <Typography
                                            fontSize={14}
                                            color="GrayText"
                                        >
                                            Clinic: {service}
                                        </Typography>
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            sx={{ mx: 1 }}
                                        />
                                        <Typography
                                            fontSize={14}
                                            color="GrayText"
                                        >
                                            แพทย์ผู้บันทึก:{" "}
                                            {item.visit
                                                ? item.visit.doctorName
                                                : "-"}
                                        </Typography>
                                    </Stack>

                                    <Divider />

                                    <Stack
                                        width={"100%"}
                                        direction={"row"}
                                        justifyContent={"space-between"}
                                        alignItems={"center"}
                                    >
                                        <Typography>History Summary</Typography>
                                        <Stack
                                            direction={"row"}
                                            gap={1}
                                            justifyContent={"end"}
                                            width={"max-content"}
                                            sx={{
                                                border: "1px solid #05AA97",
                                                borderRadius: "16px",
                                                padding: "3px 12px",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => {
                                                handleSelectAll(item.data);
                                                setCurrentIndex(index);
                                            }}
                                        >
                                            <Typography>Select All</Typography>
                                        </Stack>
                                    </Stack>

                                    <Stack direction={"column"} gap={1}>
                                        {item.data.map((e, k) => (
                                            <Stack
                                                key={k}
                                                direction={"row"}
                                                gap={1}
                                                justifyContent={"center"}
                                                width={"max-content"}
                                                sx={{
                                                    border: "1px solid #05AA97",
                                                    borderRadius: "16px",
                                                    padding: "3px 12px",
                                                    cursor: "pointer",
                                                    backgroundColor:
                                                        tempData.includes(
                                                            e.ICDDesc
                                                        )
                                                            ? theme.palette
                                                                  .primary.light
                                                            : ""
                                                }}
                                                onClick={() => {
                                                    handleSelectDetail(e);
                                                    setCurrentIndex(index);
                                                }}
                                            >
                                                {tempData.includes(
                                                    e.ICDDesc
                                                ) && (
                                                    <Typography
                                                        color={
                                                            theme.palette
                                                                .primary.main
                                                        }
                                                        sx={{
                                                            lineHeight: 0
                                                        }}
                                                    >
                                                        <CheckIcon />
                                                    </Typography>
                                                )}
                                                <Typography>
                                                    {e.ICDDesc} ({e.DiagType})
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                ) : (
                    <Box display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                )}
            </Stack>
        </>
    );
};

export default ReDiagnosis;
